import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";
import { Button, Modal } from "@mapmycustomers/ui";

import { useActivityListColumns } from "@app/component/PreviewActivityListModal/util/useActivityListColumns";
import { DownloadConfirmation } from "@app/component/PreviewListModal/DownloadConfirmation";
import { messages, viewAllMessages } from "@app/component/PreviewListModal/messages";
import { ViewAllLinkMap } from "@app/component/PreviewListModal/ViewAllLink";
import Table from "@app/component/Table";
import { showEntityView } from "@app/store/entityView/actions";
import downloadEntitiesAsCsv from "@app/util/file/downloadEntitiesAsCsv";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./PreviewActivityListModal.module.scss";

interface Props {
  activities: Activity[];
  onHide: () => void;
  onPageChange: (page: number) => void;
  page: number;
  pageSize: number;
  showEntityView: typeof showEntityView;
  title: ReactNode;
  totalRecords: number;
}

const PreviewActivityListModal: React.FC<Props> = ({
  activities,
  onHide,
  onPageChange,
  page,
  pageSize,
  showEntityView,
  title,
  totalRecords,
}) => {
  const intl = useIntl();

  const handleCompanyClick = useCallback(
    (entityId: Company["id"]) => showEntityView({ entityId, entityType: EntityType.COMPANY }),
    [showEntityView]
  );

  const handlePersonClick = useCallback(
    (entityId: Person["id"]) => showEntityView({ entityId, entityType: EntityType.PERSON }),
    [showEntityView]
  );

  const handleDealClick = useCallback(
    (entityId: Deal["id"]) => showEntityView({ entityId, entityType: EntityType.DEAL }),
    [showEntityView]
  );

  const columns = useActivityListColumns(handleCompanyClick, handlePersonClick, handleDealClick);

  const activityRowEventHandlers = useCallback(
    (activity: Activity) => ({
      onClick: ({ currentTarget }: React.MouseEvent<HTMLElement, MouseEvent>) => {
        showEntityView?.({
          elementId: getPreviewEntityUniqueElementId(currentTarget),
          entityId: activity.id,
          entityType: EntityType.ACTIVITY,
        });
      },
    }),
    [showEntityView]
  );

  const handleDownload = useCallback(() => {
    downloadEntitiesAsCsv(
      getEntityTypeDisplayName(intl, EntityType.ACTIVITY, {
        lowercase: false,
        plural: true,
      }),
      EntityType.ACTIVITY,
      activities
    );
  }, [intl, activities]);

  const ViewAllEntityLink = ViewAllLinkMap[EntityType.ACTIVITY];

  const handleChangeTable = useCallback(
    (pagination) => {
      onPageChange?.(pagination.current);
    },
    [onPageChange]
  );

  return (
    <Modal
      destroyOnClose
      footer={[
        <ViewAllEntityLink className={styles.viewAllLink} key="view">
          {intl.formatMessage(viewAllMessages[EntityType.ACTIVITY])}
        </ViewAllEntityLink>,
        <DownloadConfirmation key="download" onConfirm={handleDownload}>
          <Button type="primary">{intl.formatMessage(messages.downloadButton)}</Button>
        </DownloadConfirmation>,
      ]}
      onCancel={onHide}
      title={title}
      visible
      width="clamp(300px, 70vw, 780px)"
    >
      <Table<Activity>
        clickableRows
        columns={columns}
        dataSource={activities}
        noHover
        onChange={handleChangeTable}
        onRow={activityRowEventHandlers}
        pagination={{
          current: page,
          pageSize: pageSize,
          position: ["bottomCenter"],
          showSizeChanger: false,
          total: totalRecords,
        }}
        rowKey="id"
        scroll={{ y: 335 }}
      />
    </Modal>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(PreviewActivityListModal);
